import styles from "@/src/app/fluentui-styles";
import {
  Body1,
  Caption1,
  Checkbox,
  Field,
  Input,
  InputProps,
  makeStyles,
  mergeClasses,
  Skeleton,
  SkeletonItem,
  Slot,
  Text,
} from "@fluentui/react-components";
import { CheckmarkCircleRegular, ErrorCircleFilled } from "@fluentui/react-icons";
import React, { forwardRef, JSX, useEffect, useState } from "react";

const useStyles = makeStyles({
  ...styles,
  input: {
    ...styles.input,
    minWidth: "100%",
  },
});

export interface FormInputProps extends InputProps {
  className?: string;
  label?: string;
  required?: boolean;
  loading?: boolean;
  showEnableToggle?: boolean;
  defaultEnabled?: boolean;
  contentBefore?: string | JSX.Element;
  contentAfter?: string | JSX.Element;
  validationState?: "none" | "error" | "warning" | "success" | undefined;
  validationMessage?: Slot<"div">;
  onEnableChange?: (e: boolean) => void;
}

const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      className,
      label,
      required,
      loading,
      validationState,
      validationMessage,
      showEnableToggle = false,
      defaultEnabled = true,
      onEnableChange,
      ...rest
    },
    ref,
  ) => {
    const classes = useStyles();

    // const [enabled, setEnabled] = useState<boolean>(false);
    const [enabled, setEnabled] = useState<boolean>(defaultEnabled);

    useEffect(() => {
      onEnableChange?.(enabled);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    return (
      <div className={mergeClasses("field-text flex flex-col", className)}>
        <Field
          className={`${classes.field}`}
          label={
            label && (
              <div className="line-clamp-1">
                {showEnableToggle && (
                  <Checkbox
                    size="medium"
                    checked={enabled}
                    disabled={loading || rest.disabled}
                    onChange={(_, data) => setEnabled(data.checked ? true : false)}
                  />
                )}
                <Body1 className={enabled ? undefined : "text-gray-400"}>{label}</Body1>
                {required && <Text className="text-red-600">*</Text>}
              </div>
            )
          }
          validationState={validationState}
        >
          {loading ? (
            <div className="mt-3">
              <Skeleton>
                <SkeletonItem />
              </Skeleton>
            </div>
          ) : (
            <Input
              ref={ref}
              className={`${classes.input}`}
              {...rest}
              size="small"
              disabled={rest.disabled || !enabled}
            />
          )}
        </Field>
        <div
          className={mergeClasses(
            `flex items-center mt-[2px]`,
            validationState === "success" && "text-green-700 dark:text-green-800",
            validationState === "error" && "text-red-700 dark:text-red-800",
          )}
        >
          {validationState === "error" || validationState === "success" ? (
            <>
              <div className="flex items-center justify-center mx-1">
                {validationState === "error" && <ErrorCircleFilled />}
                {validationState === "success" && <CheckmarkCircleRegular />}
              </div>
              <Caption1>{validationMessage}</Caption1>
            </>
          ) : (
            <Caption1 className="invisible">placeholder</Caption1>
          )}
        </div>
      </div>
    );
  },
);

function areEqual(prevProps: FormInputProps, nextProps: FormInputProps) {
  return (
    prevProps.value === nextProps.value &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.loading === nextProps.loading &&
    prevProps.type === nextProps.type &&
    prevProps.validationState === nextProps.validationState &&
    prevProps.validationMessage === nextProps.validationMessage &&
    prevProps.defaultEnabled === nextProps.defaultEnabled
  );
}

FormInput.displayName = "FormInput";

export default React.memo(FormInput, areEqual);
